import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { authFetch, API_BASE } from "../auth";
import "./Analytics.css";

const API = API_BASE;
const PERIODS = ["daily", "weekly", "monthly", "yearly"];
const VIEWS = ["pnl", "by-ticker", "trades", "audit", "option-chain"];

function fmtPnl(val) {
  if (val == null) return "—";
  const s = val >= 0 ? `+$${val.toFixed(2)}` : `-$${Math.abs(val).toFixed(2)}`;
  return s;
}

function fmtUsd(val) {
  if (val == null) return "—";
  return `$${Number(val).toFixed(2)}`;
}

function pnlClass(val) {
  if (val == null) return "";
  return val >= 0 ? "analytics__pnl--positive" : "analytics__pnl--negative";
}

function cardValueClass(val) {
  if (val == null) return "analytics__card-value";
  return `analytics__card-value ${val >= 0 ? "analytics__card-value--positive" : "analytics__card-value--negative"}`;
}

function formatPeriodLabel(iso, period) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (period === "yearly") return d.getFullYear().toString();
  if (period === "monthly") return d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
  if (period === "weekly") return `Wk of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/* ── P&L Summary View ── */
function PnlSummary({ period }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    authFetch(`${API}/analytics/pnl/?period=${period}`)
      .then((r) => r.json())
      .then((j) => setData(j.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [period]);

  const totalPnl = data.reduce((s, r) => s + (r.total_pnl || 0), 0);
  const totalTrades = data.reduce((s, r) => s + (r.trade_count || 0), 0);
  const winPeriods = data.filter((r) => r.total_pnl > 0).length;

  if (loading) return <div className="analytics__loading">Loading...</div>;

  return (
    <>
      <div className="analytics__summary">
        <div className="analytics__card">
          <div className="analytics__card-label">Total Realized P&L</div>
          <div className={cardValueClass(totalPnl)}>{fmtPnl(totalPnl)}</div>
        </div>
        <div className="analytics__card">
          <div className="analytics__card-label">Closed Trades</div>
          <div className="analytics__card-value">{totalTrades}</div>
        </div>
        <div className="analytics__card">
          <div className="analytics__card-label">Profitable Periods</div>
          <div className="analytics__card-value">
            {winPeriods} / {data.length}
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="analytics__empty">No closed trades recorded yet.</div>
      ) : (
        <div className="analytics__table-wrap">
          <table className="analytics__table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Realized P&L</th>
                <th>Trades</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  <td>{formatPeriodLabel(row.period, period)}</td>
                  <td className={pnlClass(row.total_pnl)}>{fmtPnl(row.total_pnl)}</td>
                  <td>{row.trade_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

PnlSummary.propTypes = { period: PropTypes.string.isRequired };

/* ── P&L By Ticker View ── */
function PnlByTicker({ period }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    authFetch(`${API}/analytics/pnl-by-ticker/?period=${period}`)
      .then((r) => r.json())
      .then((j) => setData(j.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="analytics__loading">Loading...</div>;
  if (data.length === 0) return <div className="analytics__empty">No closed trades recorded yet.</div>;

  return (
    <div className="analytics__table-wrap">
      <table className="analytics__table">
        <thead>
          <tr>
            <th>Period</th>
            <th>Ticker</th>
            <th>Realized P&L</th>
            <th>Trades</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              <td>{formatPeriodLabel(row.period, period)}</td>
              <td style={{ fontWeight: 700, letterSpacing: "0.3px" }}>{row.ticker}</td>
              <td className={pnlClass(row.total_pnl)}>{fmtPnl(row.total_pnl)}</td>
              <td>{row.trade_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

PnlByTicker.propTypes = { period: PropTypes.string.isRequired };

/* ── Trade History View ── */
/* Health of the fill reconciler. Without this, a reconciler that died just
   looks like a day with no trades. */
function ReconcilerHealth() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      authFetch(`${API}/analytics/reconciler/`)
        .then((r) => r.json())
        .then((j) => { if (!cancelled) setInfo(j); })
        .catch(() => { if (!cancelled) setInfo(null); });
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!info) return null;

  const stale =
    !info.last_poll_at ||
    Date.now() - new Date(info.last_poll_at).getTime() > (info.poll_seconds || 15) * 4000;
  const healthy = info.is_running && !stale && info.cross_client_ok !== false;

  let detail = "Recording fills from IBKR";
  if (!info.is_running) {
    detail = `Not running — new trades are NOT being recorded${info.not_running_reason ? ` (${info.not_running_reason})` : ""}`;
  }
  else if (info.cross_client_ok === false) detail = "Only this client's executions are visible — set TWS's Master API client ID";
  else if (stale) detail = `No successful poll since ${info.last_poll_at ? new Date(info.last_poll_at).toLocaleTimeString() : "startup"}`;
  else if (info.last_error) detail = `Last error: ${info.last_error}`;

  return (
    <div
      title={detail}
      style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
        fontSize: 12, color: "var(--text-secondary)", cursor: "help",
      }}
    >
      <span
        style={{
          width: 8, height: 8, borderRadius: "50%",
          background: healthy ? "var(--success, #2e9e5b)" : "var(--danger, #c0392b)",
        }}
      />
      {healthy ? "Fill sync active" : "Fill sync problem"}
      <span style={{ opacity: 0.7 }}>— {detail}</span>
    </div>
  );
}

function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    authFetch(`${API}/analytics/trades/?limit=${limit}&offset=${offset}`)
      .then((r) => r.json())
      .then((j) => {
        setTrades(j.trades || []);
        setTotal(j.total || 0);
      })
      .catch(() => setTrades([]))
      .finally(() => setLoading(false));
  }, [offset]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="analytics__loading">Loading...</div>;
  if (trades.length === 0) {
    return (
      <>
        <ReconcilerHealth />
        <div className="analytics__empty">No trades recorded yet.</div>
      </>
    );
  }

  return (
    <>
      <ReconcilerHealth />
      <div className="analytics__table-wrap">
        <table className="analytics__table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Ticker</th>
              <th>Type</th>
              <th>Strike</th>
              <th>Expiry</th>
              <th>Qty</th>
              <th>Entry</th>
              <th>Fill Price</th>
              <th>Total</th>
              <th>Comm</th>
              <th>P&L</th>
              <th>Strategy</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => (
              <tr key={t.id}>
                <td>{new Date(t.filled_at || t.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                <td>{t.user || "—"}</td>
                <td>
                  <span className={`analytics__badge analytics__badge--${t.action.toLowerCase()}`}>
                    {t.action}
                  </span>
                </td>
                <td style={{ fontWeight: 700 }}>{t.ticker}</td>
                <td>{t.right || t.sec_type}</td>
                <td>{t.strike ? t.strike.toFixed(1) : "—"}</td>
                <td>{t.expiry || "—"}</td>
                <td>{t.quantity}</td>
                <td>{fmtUsd(t.entry_price)}</td>
                <td>
                  {fmtUsd(t.price)}
                  {!t.is_actual_fill && (
                    <span
                      title="Estimated at order placement — not an IBKR fill price"
                      style={{ marginLeft: 4, color: "var(--text-secondary)", cursor: "help" }}
                    >
                      ~
                    </span>
                  )}
                </td>
                <td>{fmtUsd(t.total_cost)}</td>
                <td>{fmtUsd(t.commission)}</td>
                <td className={pnlClass(t.realized_pnl)}>
                  {t.realized_pnl != null ? fmtPnl(t.realized_pnl) : "—"}
                </td>
                <td style={{ color: "var(--text-secondary)", fontSize: "12px" }}>{t.strategy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > limit && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "center" }}>
          <button className="analytics__tab" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
            Prev
          </button>
          <span style={{ padding: "7px 10px", color: "var(--text-secondary)", fontSize: 13 }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <button className="analytics__tab" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
            Next
          </button>
        </div>
      )}
    </>
  );
}

/* ── Audit Log View ── */
function formatPayload(log) {
  const raw = log.payload_full || log.payload_summary || "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function AuditLogView() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    authFetch(`${API}/analytics/audit-log/?limit=${limit}&offset=${offset}`)
      .then((r) => r.json())
      .then((j) => {
        setLogs(j.logs || []);
        setTotal(j.total || 0);
      })
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [offset]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="analytics__loading">Loading...</div>;
  if (logs.length === 0) return <div className="analytics__empty">No audit logs yet.</div>;

  return (
    <>
      <div className="analytics__table-wrap">
        <table className="analytics__table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Method</th>
              <th>Status</th>
              <th>Endpoint</th>
              <th>IP</th>
              <th>Payload</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => {
              const expanded = expandedId === log.id;
              const failed = log.status_code >= 400;
              const body = formatPayload(log);
              return (
                <React.Fragment key={log.id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => setExpandedId(expanded ? null : log.id)}>
                    <td>{new Date(log.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</td>
                    <td>{log.user || "—"}</td>
                    <td style={{ fontWeight: 600 }}>{log.action}</td>
                    <td>{log.method}</td>
                    <td style={{ fontWeight: 600, color: log.status_code == null ? "var(--text-muted)" : failed ? "var(--danger)" : "var(--success)" }}>
                      {log.status_code || "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{log.endpoint}</td>
                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{log.ip_address || "—"}</td>
                    <td className="analytics__audit-payload">
                      <span className="analytics__audit-caret">{expanded ? "▾" : "▸"}</span>
                      {log.payload_summary || "—"}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={8} className="analytics__audit-detail">
                        {log.error_message && (
                          <div className="analytics__audit-error">Rejected: {log.error_message}</div>
                        )}
                        <pre>{body || "No request body."}</pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > limit && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "center" }}>
          <button className="analytics__tab" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
            Prev
          </button>
          <span style={{ padding: "7px 10px", color: "var(--text-secondary)", fontSize: 13 }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <button className="analytics__tab" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
            Next
          </button>
        </div>
      )}
    </>
  );
}

/* ── Option Chain Lookup View ── */
function validateGroups(rawGroups) {
  // Convert structured form rows into API-shaped groups.
  // Each row: { pct: string|number, expiry: "YYYY-MM-DD", tickers: "AAPL,NVDA" }
  const groups = [];
  const errors = [];
  rawGroups.forEach((row, idx) => {
    const label = `Group ${idx + 1}`;
    const pct = parseFloat(row.pct);
    if (Number.isNaN(pct)) {
      errors.push(`${label}: invalid percentage`);
      return;
    }
    const expiry = (row.expiry || "").replace(/[/-]/g, "");
    if (!/^\d{8}$/.test(expiry)) {
      errors.push(`${label}: invalid expiry (pick a date)`);
      return;
    }
    const tickers = (row.tickers || "")
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean);
    if (tickers.length === 0) {
      errors.push(`${label}: no tickers`);
      return;
    }
    groups.push({ pct, expiry, tickers });
  });
  return { groups, errors };
}

const CHUNK_SIZE = 5;
const EMPTY_GROUP = { pct: "1", expiry: "", tickers: "" };

function chunkGroups(groups, size) {
  // Split each group's tickers into sub-groups of at most `size` tickers.
  const chunks = [];
  groups.forEach((g) => {
    for (let i = 0; i < g.tickers.length; i += size) {
      chunks.push({ pct: g.pct, expiry: g.expiry, tickers: g.tickers.slice(i, i + size) });
    }
  });
  return chunks;
}

function OptionChainLookup() {
  const [groupRows, setGroupRows] = useState([
    { pct: "1", expiry: "", tickers: "SPY,QQQ,AAPL,NVDA" },
    { pct: "3", expiry: "", tickers: "XOM,RTX,AVGO" },
  ]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [err, setErr] = useState("");

  const updateGroup = (idx, field, value) => {
    setGroupRows((prev) => prev.map((g, i) => (i === idx ? { ...g, [field]: value } : g)));
  };
  const addGroup = () => setGroupRows((prev) => [...prev, { ...EMPTY_GROUP }]);
  const removeGroup = (idx) => setGroupRows((prev) => prev.filter((_, i) => i !== idx));

  const submit = useCallback(async () => {
    const { groups, errors } = validateGroups(groupRows);
    if (errors.length) { setErr(errors.join("\n")); return; }
    if (groups.length === 0) { setErr("Add at least one group."); return; }
    setErr("");
    setLoading(true);
    setRows([]);

    const chunks = chunkGroups(groups, CHUNK_SIZE);
    const totalTickers = chunks.reduce((s, c) => s + c.tickers.length, 0);
    setProgress({ done: 0, total: totalTickers });

    const aggregated = [];
    let doneCount = 0;
    try {
      for (const chunk of chunks) {
        const res = await authFetch(`${API}/analytics/option-chain/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groups: [chunk] }),
        });
        const j = await res.json();
        if (j.error) {
          setErr(j.error);
          break;
        }
        aggregated.push(...(j.results || []));
        doneCount += chunk.tickers.length;
        setProgress({ done: doneCount, total: totalTickers });
        setRows([...aggregated]);
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }, [groupRows]);

  const fmtExpiry = (e) => (e && e.length === 8 ? `${e.slice(0,4)}-${e.slice(4,6)}-${e.slice(6,8)}` : e);
  const fmt = (n) => (n == null ? "—" : (typeof n === "number" ? n.toFixed(2) : n));

  return (
    <>
      <div className="opt-chain-form">
        <div className="opt-chain-form__header">
          <span className="opt-chain-form__col opt-chain-form__col--pct">% OTM</span>
          <span className="opt-chain-form__col opt-chain-form__col--expiry">Expiry</span>
          <span className="opt-chain-form__col opt-chain-form__col--tickers">Tickers (comma-separated)</span>
          <span className="opt-chain-form__col opt-chain-form__col--remove" />
        </div>

        {groupRows.map((g, idx) => (
          <div className="opt-chain-form__row" key={idx}>
            <div className="opt-chain-form__col opt-chain-form__col--pct">
              <input
                type="number"
                step="0.1"
                value={g.pct}
                onChange={(e) => updateGroup(idx, "pct", e.target.value)}
                disabled={loading}
                className="opt-chain-form__input"
              />
              <span className="opt-chain-form__suffix">%</span>
            </div>
            <div className="opt-chain-form__col opt-chain-form__col--expiry">
              <input
                type="date"
                value={g.expiry}
                onChange={(e) => updateGroup(idx, "expiry", e.target.value)}
                disabled={loading}
                className="opt-chain-form__input"
              />
            </div>
            <div className="opt-chain-form__col opt-chain-form__col--tickers">
              <input
                type="text"
                value={g.tickers}
                onChange={(e) => updateGroup(idx, "tickers", e.target.value.toUpperCase())}
                disabled={loading}
                placeholder="SPY,QQQ,AAPL"
                spellCheck={false}
                className="opt-chain-form__input"
              />
            </div>
            <div className="opt-chain-form__col opt-chain-form__col--remove">
              <button
                type="button"
                className="opt-chain-form__remove"
                onClick={() => removeGroup(idx)}
                disabled={loading || groupRows.length === 1}
                title="Remove group"
              >
                {"✕"}
              </button>
            </div>
          </div>
        ))}

        <div className="opt-chain-form__actions">
          <button
            type="button"
            className="analytics__tab"
            onClick={addGroup}
            disabled={loading}
          >
            + Add Group
          </button>
          <button
            type="button"
            className="analytics__tab analytics__tab--active"
            onClick={submit}
            disabled={loading}
          >
            {loading ? "Fetching..." : "Fetch Chains"}
          </button>
          {rows.length > 0 && !loading && (
            <button type="button" className="analytics__tab" onClick={() => setRows([])}>Clear Results</button>
          )}
          {loading && progress.total > 0 && (
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              {progress.done} / {progress.total} tickers
            </span>
          )}
        </div>
        {err && (
          <pre style={{ color: "var(--danger, #e55)", margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>{err}</pre>
        )}
      </div>

      {rows.length === 0 && !loading ? (
        <div className="analytics__empty">Enter groups above and click Fetch Chains.</div>
      ) : (
        <div className="analytics__table-wrap">
          <table className="analytics__table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Pct</th>
                <th>Expiry</th>
                <th>Current</th>
                <th>Call Strike</th>
                <th>Call Premium</th>
                <th>Put Strike</th>
                <th>Put Premium</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 700 }}>{r.symbol}</td>
                  <td>{r.pct}%</td>
                  <td>{fmtExpiry(r.expiry)}</td>
                  <td>{fmt(r.current_price)}</td>
                  <td>{fmt(r.call_strike)}</td>
                  <td>{fmt(r.call_premium)}</td>
                  <td>{fmt(r.put_strike)}</td>
                  <td>{fmt(r.put_premium)}</td>
                  <td style={{ color: r.error ? "var(--danger, #e55)" : "var(--text-secondary)", fontSize: 12 }}>
                    {r.error || "OK"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ── Main Analytics Page ── */
export default function Analytics() {
  const [period, setPeriod] = useState("daily");
  const [view, setView] = useState("pnl");

  return (
    <div>
      <div className="page-header">
        <h2 className="page-header__title">Analytics</h2>
        <p className="page-header__subtitle">Trade performance and audit trail.</p>
      </div>

      <div className="analytics__controls">
        <div className="analytics__tab-group">
          {VIEWS.map((v) => (
            <button
              key={v}
              className={`analytics__tab ${view === v ? "analytics__tab--active" : ""}`}
              onClick={() => setView(v)}
            >
              {v === "pnl" ? "P&L Summary" : v === "by-ticker" ? "By Ticker" : v === "trades" ? "Trade Log" : v === "audit" ? "Audit Log" : "Option Chain"}
            </button>
          ))}
        </div>

        {(view === "pnl" || view === "by-ticker") && (
          <div className="analytics__view-group">
            {PERIODS.map((p) => (
              <button
                key={p}
                className={`analytics__tab ${period === p ? "analytics__tab--active" : ""}`}
                onClick={() => setPeriod(p)}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "pnl" && <PnlSummary period={period} />}
      {view === "by-ticker" && <PnlByTicker period={period} />}
      {view === "trades" && <TradeHistory />}
      {view === "audit" && <AuditLogView />}
      {view === "option-chain" && <OptionChainLookup />}
    </div>
  );
}
